import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import companyBg from '../assets/company-bg.jpg';
import TeamSection from '../pages/TeamSection';
import SecurityComplianceSection from '../components/SecurityComplianceSection';
import GlobalLocationsSection from '../pages/GlobalLocationsSection';
import ConsultationCTA from '../components/ConsultationCTA';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const Company = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "MDeRCM Solutions LLC",
    "alternateName": ["MDeRCM", "Medical Revenue Cycle Management"],
    "url": "https://mdercm.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://mdercm.com/logo192.png",
      "width": 192,
      "height": 192
    },
    "description": "Leading revenue cycle management services company providing AI-powered medical billing outsourcing for small practices, independent physicians, and multispecialty practices. Expert denial management services to reduce claim denials and comprehensive RCM solutions for healthcare providers across the USA.",
    "foundingDate": "2020",
    "slogan": "Transforming Healthcare Revenue Cycles with AI",
    "address": [
      {
        "@type": "PostalAddress",
        "name": "USA Headquarters",
        "streetAddress": "525 Route 73 North, Suite 104",
        "addressLocality": "Marlton",
        "addressRegion": "NJ",
        "postalCode": "08053",
        "addressCountry": "US"
      },
      {
        "@type": "PostalAddress",
        "name": "India Office",
        "streetAddress": "DLF Cyber City",
        "addressLocality": "Gurugram",
        "addressRegion": "Haryana",
        "postalCode": "122002",
        "addressCountry": "IN"
      }
    ],
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+1-510-356-6069",
        "contactType": "Customer Service",
        "areaServed": "US",
        "availableLanguage": ["English"],
        "contactOption": "TollFree"
      },
      {
        "@type": "ContactPoint",
        "telephone": "+91-9311250055",
        "contactType": "Customer Service",
        "areaServed": "IN",
        "availableLanguage": ["English", "Hindi"]
      }
    ],
    "sameAs": [
      "https://www.linkedin.com/company/mdercm",
      "https://twitter.com/mdercm",
      "https://www.facebook.com/mdercm"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Healthcare Revenue Cycle Management Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "AI-Powered Revenue Cycle Management",
            "description": "Comprehensive revenue cycle management services with AI automation"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Medical Billing Outsourcing",
            "description": "End-to-end medical billing and coding outsourcing services"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Healthcare Cost Reduction Consulting",
            "description": "Strategic consulting to reduce healthcare operational costs by 30-40%"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Denial Management Services for Medical Clinics",
            "description": "Expert denial management to reduce claim denials and maximize revenue"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "RCM for Independent Physicians & Small Practices",
            "description": "Specialized revenue cycle management for independent physicians and small medical practices"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Revenue Cycle Management for Multispecialty Practices",
            "description": "Comprehensive RCM solutions tailored for multispecialty healthcare practices"
          }
        }
      ]
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://mdercm.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Company",
        "item": "https://mdercm.com/company"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>About MDeRCM | Top RCM Company USA | MDeRCM</title>
        <meta 
          name="description" 
          content="MDeRCM is a leading AI-powered RCM company. Expert medical billing, denial management & healthcare automation. 200+ practices, 98% satisfaction." 
        />
        <meta 
          name="keywords" 
          content="revenue cycle management services companies, best revenue cycle management companies, medical billing outsourcing companies, healthcare cost reduction consulting, RCM company USA, AI-powered RCM, hospital revenue cycle management, medical billing outsourcing for small practices, reduce claim denials in healthcare billing, revenue cycle management for independent physicians, denial management services for medical clinics, revenue cycle management for multispecialty practices, benefits of medical billing outsourcing for clinics" 
        />
        <link rel="canonical" href="https://mdercm.com/company" />
        
        {/* Open Graph */}
        <meta property="og:title" content="About MDeRCM - Leading Healthcare Revenue Cycle Management Company" />
        <meta property="og:description" content="Leading RCM services company transforming healthcare with AI-driven revenue cycle solutions and medical billing outsourcing. Serving 500+ healthcare providers across the USA." />
        <meta property="og:url" content="https://mdercm.com/company" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://mdercm.com/company-bg.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About MDeRCM - Revenue Cycle Management Experts" />
        <meta name="twitter:description" content="AI-powered revenue cycle management services for hospitals and clinics across the USA." />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          color: 'white',
          backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.9), rgba(0,0,0,0.5)), url(${companyBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          padding: '80px 5%',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          perspective: '1000px'
        }}
      >
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          style={{ maxWidth: '1200px', zIndex: 2 }}
        >
          <motion.h1
            style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 700, lineHeight: 1.3, marginBottom: '20px' }}
            whileHover={{ scale: 1.02 }}
          >
            About <span style={{ color: '#F26522' }}>MDeRCM Solutions LLC</span>
          </motion.h1>

          <motion.h2
            style={{ fontSize: 'clamp(20px, 3vw, 28px)', fontWeight: 600, lineHeight: 1.4, color: '#4facfe', marginBottom: '30px' }}
            whileInView={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            transition={{ delay: 0.2 }}
          >
            Leading Revenue Cycle Management Services for Independent Physicians, Small Practices & Multispecialty Clinics Across the USA
          </motion.h2>

          <motion.p
            style={{ fontSize: 'clamp(16px, 2vw, 18px)', marginTop: '24px', lineHeight: 1.8, color: '#e0e0e0' }}
            whileInView={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            transition={{ delay: 0.3 }}
          >
            MDeRCM is transforming the healthcare industry with <Link to="/healthcare/ai" style={{ color: '#4facfe', textDecoration: 'none', fontWeight: '600' }}>AI-driven revenue cycle solutions</Link>. 
            As one of the best revenue cycle management companies in the USA, we specialize in <strong>medical billing outsourcing for small practices</strong> and independent physicians. Our expert <strong>denial management services for medical clinics</strong> help reduce claim denials, increase claim success rates by 25-35%, and streamline operations — all with intelligent automation and zero hassle.
          </motion.p>

          <motion.p
            style={{ fontSize: 'clamp(16px, 2vw, 18px)', marginTop: '20px', lineHeight: 1.8, color: '#e0e0e0' }}
            whileInView={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            transition={{ delay: 0.4 }}
          >
            Our comprehensive <strong>revenue cycle management for multispecialty practices</strong> and independent physicians combines cutting-edge AI technology with deep industry expertise. We deliver <Link to="/blog/reducing-healthcare-costs-ai-rcm" style={{ color: '#4facfe', textDecoration: 'none' }}>30-40% cost reductions</Link> while improving collection rates and cash flow. Experience the <strong>benefits of medical billing outsourcing for clinics</strong> with our proven strategies that reduce claim denials in healthcare billing.
          </motion.p>

          <motion.div
            style={{
              marginTop: '40px',
              backgroundColor: '#00283A',
              padding: '30px 35px',
              borderRadius: '12px',
              maxWidth: '900px',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)'
            }}
            whileHover={{ rotateY: 3 }}
            transition={{ type: 'spring', stiffness: 100 }}
          >
            <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '15px', color: '#F26522' }}>
              Our Mission
            </h3>
            <p style={{ fontSize: '16px', lineHeight: 1.7, color: '#e0e0e0' }}>
              Join us in revolutionizing healthcare workflows — empowering providers, improving patient outcomes, and ensuring patients get the care they deserve, faster. Through strategic <Link to="/blog/medical-billing-outsourcing-guide" style={{ color: '#4facfe', textDecoration: 'none' }}>medical billing outsourcing</Link> and advanced technology, we're building the future of healthcare financial management.
            </p>
          </motion.div>
        </motion.div>

        {/* Key Statistics Section */}
        <motion.div
          style={{
            marginTop: '60px',
            maxWidth: '1200px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '25px',
            zIndex: 2
          }}
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.5 }}
        >
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            padding: '30px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{ fontSize: '42px', fontWeight: '700', color: '#4facfe', marginBottom: '10px' }}>200+</div>
            <div style={{ fontSize: '16px', color: '#e0e0e0' }}>Healthcare Practices</div>
          </div>

          <div style={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            padding: '30px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{ fontSize: '42px', fontWeight: '700', color: '#4facfe', marginBottom: '10px' }}>$187M+</div>
            <div style={{ fontSize: '16px', color: '#e0e0e0' }}>Revenue Managed Annually</div>
          </div>

          <div style={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            padding: '30px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{ fontSize: '42px', fontWeight: '700', color: '#4facfe', marginBottom: '10px' }}>96%+</div>
            <div style={{ fontSize: '16px', color: '#e0e0e0' }}>Average Clean Claims Rate</div>
          </div>

          <div style={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            padding: '30px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{ fontSize: '42px', fontWeight: '700', color: '#4facfe', marginBottom: '10px' }}>40%</div>
            <div style={{ fontSize: '16px', color: '#e0e0e0' }}>Average Cost Reduction</div>
          </div>
        </motion.div>

        <TeamSection />
      </div>

      {/* Why Choose MDeRCM Section */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '80px 5%',
        fontFamily: "'Poppins', sans-serif"
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(28px, 4vw, 36px)',
            fontWeight: '700',
            color: '#00203F',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            Why Leading Healthcare Organizations Choose MDeRCM
          </h2>
          <p style={{
            fontSize: '18px',
            color: '#666',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 50px',
            lineHeight: '1.8'
          }}>
            As one of the best revenue cycle management companies in the USA, we deliver measurable results through innovation, expertise, and unwavering commitment to our clients' success.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px',
            marginTop: '40px'
          }}>
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '30px',
              borderRadius: '12px',
              borderLeft: '5px solid #4facfe'
            }}>
              <h3 style={{ fontSize: '22px', color: '#00203F', marginBottom: '15px', fontWeight: '700' }}>
                🤖 AI-Powered Technology
              </h3>
              <p style={{ fontSize: '16px', color: '#555', lineHeight: '1.7' }}>
                Our proprietary AI platform automates 80% of routine RCM tasks, reducing costs by 30-40% while improving accuracy to 99.9%. We combine machine learning, NLP, and predictive analytics to optimize every aspect of the revenue cycle.
              </p>
            </div>

            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '30px',
              borderRadius: '12px',
              borderLeft: '5px solid #4caf50'
            }}>
              <h3 style={{ fontSize: '22px', color: '#00203F', marginBottom: '15px', fontWeight: '700' }}>
                📊 Proven Results
              </h3>
              <p style={{ fontSize: '16px', color: '#555', lineHeight: '1.7' }}>
                Our clients achieve 96%+ clean claims rates, under 30 days in A/R, and 25-35% increases in collections. These aren't projections—they're actual results from hundreds of healthcare providers across the USA.
              </p>
            </div>

            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '30px',
              borderRadius: '12px',
              borderLeft: '5px solid #ff9800'
            }}>
              <h3 style={{ fontSize: '22px', color: '#00203F', marginBottom: '15px', fontWeight: '700' }}>
                🏥 Healthcare Expertise
              </h3>
              <p style={{ fontSize: '16px', color: '#555', lineHeight: '1.7' }}>
                Our team includes certified medical coders, healthcare finance experts, and RCM specialists with decades of combined experience. We understand the unique challenges of hospitals, clinics, and specialty practices.
              </p>
            </div>

            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '30px',
              borderRadius: '12px',
              borderLeft: '5px solid #e91e63'
            }}>
              <h3 style={{ fontSize: '22px', color: '#00203F', marginBottom: '15px', fontWeight: '700' }}>
                🛡️ Security & Compliance
              </h3>
              <p style={{ fontSize: '16px', color: '#555', lineHeight: '1.7' }}>
                HIPAA-compliant infrastructure, ISO 9001 & 27001, and regular security audits ensure your data is protected. We maintain the highest standards of healthcare data security and regulatory compliance.
              </p>
            </div>

            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '30px',
              borderRadius: '12px',
              borderLeft: '5px solid #9c27b0'
            }}>
              <h3 style={{ fontSize: '22px', color: '#00203F', marginBottom: '15px', fontWeight: '700' }}>
                💼 Comprehensive Services
              </h3>
              <p style={{ fontSize: '16px', color: '#555', lineHeight: '1.7' }}>
                From <Link to="/healthcare/ai/eligibility-check" style={{ color: '#4facfe', textDecoration: 'none' }}>eligibility verification</Link> to <Link to="/healthcare/ai/accounts-receivable" style={{ color: '#4facfe', textDecoration: 'none' }}>A/R management</Link>, we handle every aspect of the revenue cycle. Full-service or modular solutions tailored to your needs.
              </p>
            </div>

            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '30px',
              borderRadius: '12px',
              borderLeft: '5px solid #00bcd4'
            }}>
              <h3 style={{ fontSize: '22px', color: '#00203F', marginBottom: '15px', fontWeight: '700' }}>
                🌐 24/7 Support
              </h3>
              <p style={{ fontSize: '16px', color: '#555', lineHeight: '1.7' }}>
                Round-the-clock support with dedicated account managers, real-time dashboards, and monthly performance reviews. We're available whenever you need us, with offices in the USA and India.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Our Approach Section */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '80px 5%',
        fontFamily: "'Poppins', sans-serif"
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(28px, 4vw, 36px)',
            fontWeight: '700',
            color: '#00203F',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            Our Approach to Revenue Cycle Excellence
          </h2>
          <p style={{
            fontSize: '18px',
            color: '#666',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 50px',
            lineHeight: '1.8'
          }}>
            We don't just process claims—we partner with healthcare organizations to transform their entire revenue cycle through strategic consulting and advanced technology.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '30px'
          }}>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#4facfe',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px',
                fontWeight: '700',
                margin: '0 auto 20px'
              }}>
                1
              </div>
              <h3 style={{ fontSize: '20px', color: '#00203F', marginBottom: '15px', fontWeight: '700' }}>
                Assessment & Analysis
              </h3>
              <p style={{ fontSize: '16px', color: '#555', lineHeight: '1.7' }}>
                Comprehensive audit of your current RCM processes identifying revenue leakage, inefficiencies, and improvement opportunities worth $500K-$2M annually.
              </p>
            </div>

            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#4caf50',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px',
                fontWeight: '700',
                margin: '0 auto 20px'
              }}>
                2
              </div>
              <h3 style={{ fontSize: '20px', color: '#00203F', marginBottom: '15px', fontWeight: '700' }}>
                Custom Strategy Development
              </h3>
              <p style={{ fontSize: '16px', color: '#555', lineHeight: '1.7' }}>
                Tailored implementation plan with 60/90/180-day milestones, specific KPI targets, and ROI projections based on your practice's unique characteristics.
              </p>
            </div>

            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#ff9800',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px',
                fontWeight: '700',
                margin: '0 auto 20px'
              }}>
                3
              </div>
              <h3 style={{ fontSize: '20px', color: '#00203F', marginBottom: '15px', fontWeight: '700' }}>
                Seamless Implementation
              </h3>
              <p style={{ fontSize: '16px', color: '#555', lineHeight: '1.7' }}>
                Smooth 30-45 day transition with zero disruption to cash flow. Our experienced team handles data migration, system integration, and staff training.
              </p>
            </div>

            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#e91e63',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px',
                fontWeight: '700',
                margin: '0 auto 20px'
              }}>
                4
              </div>
              <h3 style={{ fontSize: '20px', color: '#00203F', marginBottom: '15px', fontWeight: '700' }}>
                Continuous Optimization
              </h3>
              <p style={{ fontSize: '16px', color: '#555', lineHeight: '1.7' }}>
                Ongoing performance monitoring, monthly reviews, and AI model refinement to ensure sustained excellence and continuous improvement.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Services for Different Practice Types - New Section for SEO Keywords */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '80px 5%',
        fontFamily: "'Poppins', sans-serif"
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(28px, 4vw, 36px)',
            fontWeight: '700',
            color: '#00203F',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            Specialized RCM Solutions for Every Practice Type
          </h2>
          <p style={{
            fontSize: '18px',
            color: '#666',
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto 50px',
            lineHeight: '1.8'
          }}>
            From independent physicians to multispecialty practices, we deliver customized revenue cycle management and denial management services
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px',
            marginTop: '40px'
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              border: '2px solid #4facfe'
            }}>
              <div style={{
                fontSize: '40px',
                marginBottom: '15px'
              }}>👨‍⚕️</div>
              <h3 style={{
                fontSize: '22px',
                color: '#00203F',
                marginBottom: '15px',
                fontWeight: '700'
              }}>
                Medical Billing Outsourcing for Small Practices
              </h3>
              <p style={{
                fontSize: '16px',
                color: '#555',
                lineHeight: '1.7',
                marginBottom: '15px'
              }}>
                Affordable, comprehensive medical billing outsourcing designed specifically for small practices and independent physicians. Get enterprise-level RCM services without the enterprise cost.
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                color: '#666',
                fontSize: '15px',
                lineHeight: '2'
              }}>
                <li>✓ No upfront costs or setup fees</li>
                <li>✓ Dedicated billing specialists</li>
                <li>✓ 95%+ clean claim rate</li>
                <li>✓ Real-time analytics dashboard</li>
              </ul>
            </div>

            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              border: '2px solid #F26522'
            }}>
              <div style={{
                fontSize: '40px',
                marginBottom: '15px'
              }}>🏥</div>
              <h3 style={{
                fontSize: '22px',
                color: '#00203F',
                marginBottom: '15px',
                fontWeight: '700'
              }}>
                Revenue Cycle Management for Independent Physicians
              </h3>
              <p style={{
                fontSize: '16px',
                color: '#555',
                lineHeight: '1.7',
                marginBottom: '15px'
              }}>
                Complete revenue cycle management tailored for independent physicians who want to focus on patient care, not paperwork. Our denial management services ensure maximum reimbursement.
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                color: '#666',
                fontSize: '15px',
                lineHeight: '2'
              }}>
                <li>✓ Reduce claim denials by 40%</li>
                <li>✓ Faster payment cycles (15-20 days)</li>
                <li>✓ HIPAA-compliant systems</li>
                <li>✓ Transparent pricing</li>
              </ul>
            </div>

            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              border: '2px solid #00BFA6'
            }}>
              <div style={{
                fontSize: '40px',
                marginBottom: '15px'
              }}>🏢</div>
              <h3 style={{
                fontSize: '22px',
                color: '#00203F',
                marginBottom: '15px',
                fontWeight: '700'
              }}>
                Revenue Cycle Management for Multispecialty Practices
              </h3>
              <p style={{
                fontSize: '16px',
                color: '#555',
                lineHeight: '1.7',
                marginBottom: '15px'
              }}>
                Comprehensive RCM solutions for multispecialty practices handling complex billing across multiple specialties. Expert denial management services for medical clinics of all sizes.
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                color: '#666',
                fontSize: '15px',
                lineHeight: '2'
              }}>
                <li>✓ Multi-specialty billing expertise</li>
                <li>✓ Advanced denial management</li>
                <li>✓ Credentialing & contracting support</li>
                <li>✓ 25-35% revenue increase</li>
              </ul>
            </div>
          </div>

          <div style={{
            marginTop: '50px',
            padding: '40px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '2px solid #e0e0e0'
          }}>
            <h3 style={{
              fontSize: '24px',
              color: '#00203F',
              marginBottom: '20px',
              fontWeight: '700',
              textAlign: 'center'
            }}>
              Benefits of Medical Billing Outsourcing for Clinics
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '25px',
              marginTop: '30px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>💰</div>
                <h4 style={{ fontSize: '18px', color: '#00203F', marginBottom: '10px', fontWeight: '600' }}>
                  Reduce Operating Costs by 30-40%
                </h4>
                <p style={{ fontSize: '15px', color: '#666' }}>
                  Eliminate in-house billing staff expenses
                </p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>📈</div>
                <h4 style={{ fontSize: '18px', color: '#00203F', marginBottom: '10px', fontWeight: '600' }}>
                  Reduce Claim Denials in Healthcare Billing
                </h4>
                <p style={{ fontSize: '15px', color: '#666' }}>
                  AI-powered claim scrubbing prevents denials
                </p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>⚡</div>
                <h4 style={{ fontSize: '18px', color: '#00203F', marginBottom: '10px', fontWeight: '600' }}>
                  Faster Cash Flow
                </h4>
                <p style={{ fontSize: '15px', color: '#666' }}>
                  Get paid in 15-20 days vs industry 30-45 days
                </p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>🎯</div>
                <h4 style={{ fontSize: '18px', color: '#00203F', marginBottom: '10px', fontWeight: '600' }}>
                  Focus on Patient Care
                </h4>
                <p style={{ fontSize: '15px', color: '#666' }}>
                  Let experts handle your billing operations
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Industries Served */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '80px 5%',
        fontFamily: "'Poppins', sans-serif"
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(28px, 4vw, 36px)',
            fontWeight: '700',
            color: '#00203F',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            Healthcare Specialties We Serve
          </h2>
          <p style={{
            fontSize: '18px',
            color: '#666',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 50px',
            lineHeight: '1.8'
          }}>
            Our revenue cycle management services support healthcare providers across all major specialties
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '20px'
          }}>
            {[
              '🏥 Hospitals & Health Systems',
              '👨‍⚕️ Primary Care Practices',
              '❤️ Cardiology',
              '🦴 Orthopedics',
              '🧠 Neurology',
              '👶 Pediatrics',
              '🩺 Internal Medicine',
              '🏃 Physical Therapy',
              '🦷 Dental Practices',
              '🧘 Mental Health',
              '👁️ Ophthalmology',
              '🔬 Oncology'
            ].map((specialty, index) => (
              <div key={index} style={{
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '16px',
                color: '#00203F',
                fontWeight: '600',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease'
              }}>
                {specialty}
              </div>
            ))}
          </div>
        </div>
      </div>

      <SecurityComplianceSection />
      <GlobalLocationsSection />
      
      {/* Related Links Section for SEO */}
      <div style={{
        backgroundColor: '#f0f8ff',
        padding: '60px 5%',
        fontFamily: "'Poppins', sans-serif"
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#00203F',
            marginBottom: '30px',
            textAlign: 'center'
          }}>
            Learn More About Our Services
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            <Link to="/healthcare/ai" style={{
              textDecoration: 'none',
              backgroundColor: 'white',
              padding: '25px',
              borderRadius: '10px',
              border: '1px solid #e0e0e0',
              display: 'block',
              transition: 'all 0.3s ease'
            }}>
              <h3 style={{ fontSize: '18px', color: '#00203F', marginBottom: '10px', fontWeight: '700' }}>
                AI Healthcare Solutions
              </h3>
              <p style={{ fontSize: '15px', color: '#666', margin: 0 }}>
                Explore our comprehensive AI-powered RCM platform
              </p>
            </Link>

            <Link to="/blog/maximizing-healthcare-returns" style={{
              textDecoration: 'none',
              backgroundColor: 'white',
              padding: '25px',
              borderRadius: '10px',
              border: '1px solid #e0e0e0',
              display: 'block',
              transition: 'all 0.3s ease'
            }}>
              <h3 style={{ fontSize: '18px', color: '#00203F', marginBottom: '10px', fontWeight: '700' }}>
                Maximizing Healthcare Returns
              </h3>
              <p style={{ fontSize: '15px', color: '#666', margin: 0 }}>
                Learn how to increase collections by 25-35%
              </p>
            </Link>

            <Link to="/blog/medical-billing-outsourcing-guide" style={{
              textDecoration: 'none',
              backgroundColor: 'white',
              padding: '25px',
              borderRadius: '10px',
              border: '1px solid #e0e0e0',
              display: 'block',
              transition: 'all 0.3s ease'
            }}>
              <h3 style={{ fontSize: '18px', color: '#00203F', marginBottom: '10px', fontWeight: '700' }}>
                Medical Billing Outsourcing Guide
              </h3>
              <p style={{ fontSize: '15px', color: '#666', margin: 0 }}>
                Complete guide to outsourcing medical billing
              </p>
            </Link>

            <Link to="/free-trial" style={{
              textDecoration: 'none',
              backgroundColor: 'white',
              padding: '25px',
              borderRadius: '10px',
              border: '2px solid #4facfe',
              display: 'block',
              transition: 'all 0.3s ease'
            }}>
              <h3 style={{ fontSize: '18px', color: '#4facfe', marginBottom: '10px', fontWeight: '700' }}>
                Get Free Consultation
              </h3>
              <p style={{ fontSize: '15px', color: '#666', margin: 0 }}>
                Schedule your complimentary RCM assessment today
              </p>
            </Link>
          </div>
        </div>
      </div>

      <ConsultationCTA />
    </>
  );
};

export default Company;